// Client cache of the account limit book. Instant clicks still sign in this
// browser. Limit fills sign on the server from the same primary v5r1.

import type { LimitRetryPolicy, LimitRetryState } from './limitRetry'
import { DEFAULT_TRADE_PRESETS } from './tradePanel'

export type LimitOrderSide = 'buy' | 'sell'
export type LimitOrderStatus = 'open' | 'filled' | 'canceled' | 'failed'

export type LimitOrder = {
  id: string
  wallet: string
  pairAddr: string
  baseToken: string
  ticker: string
  dex: string
  side: LimitOrderSide
  amountLabel: string
  offerNano: string
  limitMcapUsd: number
  limitPriceTon: number | null
  slippageBps: number
  retryPolicy?: LimitRetryPolicy
  retryState?: LimitRetryState
  /** 0–2 when placed from P1/P2/P3. Older rows omit it. */
  presetIndex?: number
  buySlippageBps?: number
  sellSlippageBps?: number
  status: LimitOrderStatus
  failReason?: string
  txHash?: string
  triggeredMcapUsd?: number | null
  filledMcapUsd?: number | null
  filledAt?: number | null
  createdAt: number
  updatedAt: number
}

export type PlaceLimitOrderInput = {
  wallet: string
  pairAddr: string
  baseToken: string
  ticker: string
  dex: string
  side: LimitOrderSide
  amountLabel: string
  offerNano: bigint
  limitMcapUsd: number
  limitPriceTon: number | null
  slippageBps: number
  retryPolicy?: LimitRetryPolicy
  presetIndex?: number
  buySlippageBps?: number
  sellSlippageBps?: number
}

const DB_NAME = 'wils.limit-orders'
const STORE = 'orders'
const VERSION = 1

/** Why a limit order may not be placed with this target. */
export type LimitTargetRefusal = 'zero' | 'no-live-mc' | 'buy-above' | 'sell-below'

/**
 * MONEY PATH. Why the form refuses to place a limit order with this target
 * market cap, or null when it may be placed. The server fills a buy when the
 * market cap is AT OR BELOW its target and a sell when it is AT OR ABOVE it
 * (backend limitfill/trigger.go, PriceCrossedLimit), on the first trade after
 * the order is made. So:
 *   - with no market cap now to compare with, the side of the target is not
 *     known — it is not placed on a guess (said first: it is the reason, an
 *     empty target then is only its consequence);
 *   - a target that is not above zero (the slider's −100%) can never be placed;
 *   - a buy ABOVE the market cap now, or a sell BELOW it, would fill at the
 *     next trade: a market order in a limit's clothes — refused (the operator,
 *     2026-10-18). A target AT the market cap now is not above or below it and
 *     is placed, as it always was (the form opens on it).
 * The server does not refuse these yet: this is the client's own check.
 */
export function limitTargetRefusal(
  side: LimitOrderSide,
  targetMcUsd: number,
  liveMcUsd: number | null
): LimitTargetRefusal | null {
  if (liveMcUsd == null || !(Number.isFinite(liveMcUsd) && liveMcUsd > 0)) return 'no-live-mc'
  if (!(Number.isFinite(targetMcUsd) && targetMcUsd > 0)) return 'zero'
  if (side === 'buy' && targetMcUsd > liveMcUsd) return 'buy-above'
  if (side === 'sell' && targetMcUsd < liveMcUsd) return 'sell-below'
  return null
}

/** The one plain line the form says a refusal with (and its toast). */
export function limitTargetRefusalText(refusal: LimitTargetRefusal): string {
  switch (refusal) {
    case 'zero':
      return 'A $0 target cannot be placed'
    case 'no-live-mc':
      return 'No MC now — the target cannot be checked'
    case 'buy-above':
      return 'A buy above the MC now would fill at once'
    case 'sell-below':
      return 'A sell below the MC now would fill at once'
  }
}

export function priceCrossedLimit(
  order: Pick<LimitOrder, 'side' | 'limitPriceTon' | 'limitMcapUsd'>,
  live: {
    priceTon?: number | null
    mcapUsd?: number | null
  }
): boolean {
  if (
    live.mcapUsd != null &&
    Number.isFinite(live.mcapUsd) &&
    live.mcapUsd > 0 &&
    Number.isFinite(order.limitMcapUsd) &&
    order.limitMcapUsd > 0
  ) {
    return order.side === 'buy'
      ? live.mcapUsd <= order.limitMcapUsd
      : live.mcapUsd >= order.limitMcapUsd
  }
  return live.priceTon != null &&
    order.limitPriceTon != null &&
    Number.isFinite(live.priceTon) &&
    Number.isFinite(order.limitPriceTon) &&
    live.priceTon > 0 &&
    order.limitPriceTon > 0
    ? order.side === 'buy'
      ? live.priceTon <= order.limitPriceTon
      : live.priceTon >= order.limitPriceTon
    : false
}

export function newLimitOrder(input: PlaceLimitOrderInput): LimitOrder {
  const now = Date.now()
  return {
    id: `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    wallet: input.wallet,
    pairAddr: input.pairAddr,
    baseToken: input.baseToken,
    ticker: input.ticker,
    dex: input.dex,
    side: input.side,
    amountLabel: input.amountLabel,
    offerNano: input.offerNano.toString(),
    limitMcapUsd: input.limitMcapUsd,
    limitPriceTon: input.limitPriceTon,
    slippageBps: input.slippageBps,
    retryPolicy: input.retryPolicy?.enabled ? input.retryPolicy : undefined,
    presetIndex: input.presetIndex,
    buySlippageBps: input.buySlippageBps,
    sellSlippageBps: input.sellSlippageBps,
    status: 'open',
    createdAt: now,
    updatedAt: now,
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) {
      reject(new Error('IndexedDB unavailable'))
      return
    }
    const request = globalThis.indexedDB.open(DB_NAME, VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('wallet', 'wallet', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('open limit-order db'))
  })
}

export async function loadLimitOrders(wallet: string): Promise<LimitOrder[]> {
  const rows = await loadAllLimitOrders()
  return rows.filter((row) => row.wallet === wallet)
}

export async function loadAllLimitOrders(): Promise<LimitOrder[]> {
  const db = await openDb()
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly')
      const req = tx.objectStore(STORE).getAll()
      req.onsuccess = () => {
        const rows = (req.result as LimitOrder[]) ?? []
        rows.sort((a, b) => b.createdAt - a.createdAt)
        resolve(rows)
      }
      req.onerror = () => reject(req.error ?? new Error('load limit orders'))
    })
  } finally {
    db.close()
  }
}

/** The local cache becomes exactly these rows (the server's book): rows of an
 *  earlier session, or another account, do not survive a read. */
export async function replaceLimitOrders(rows: readonly LimitOrder[]): Promise<void> {
  const db = await openDb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('replace limit orders'))
      const store = tx.objectStore(STORE)
      store.clear()
      for (const row of rows) store.put(row)
    })
  } finally {
    db.close()
  }
}

/** Forgets every cached order: on log-out, so the next account on this
 *  device never sees, nor sends, this one's book. */
export async function clearLimitOrders(): Promise<void> {
  await replaceLimitOrders([])
}

export async function saveLimitOrder(order: LimitOrder): Promise<void> {
  const db = await openDb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('save limit order'))
      tx.objectStore(STORE).put(order)
    })
  } finally {
    db.close()
  }
}

export async function patchLimitOrder(
  id: string,
  patch: Partial<Pick<LimitOrder, 'status' | 'failReason' | 'txHash'>>
): Promise<LimitOrder | null> {
  const db = await openDb()
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      const get = store.get(id)
      get.onsuccess = () => {
        const current = get.result as LimitOrder | undefined
        if (!current) {
          resolve(null)
          return
        }
        const next: LimitOrder = { ...current, ...patch, updatedAt: Date.now() }
        store.put(next)
        resolve(next)
      }
      get.onerror = () => reject(get.error ?? new Error('patch limit order'))
    })
  } finally {
    db.close()
  }
}

/** A slippage in bps as the book says it: "1%", "1.5%". */
export function fmtBps(bps: number): string {
  const pct = bps / 100
  return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`
}

/** P1 (1% / 1.5%) — buy / sell of the preset used at place, as that preset
 *  TRADES: each side held to its cap. Display only — nothing here changes an
 *  order. The side the order executes on is always its own slippageBps (what
 *  it goes out on: slippageForSide at place); a row that recorded the
 *  preset's raw figure for that side (before the form recorded the held ones,
 *  review of round 5) said 9% over an order that executes on 4%. The other
 *  side is what the row recorded: the order carries no cap to hold it to. */
export function limitOrderModeLabel(order: LimitOrder): string {
  const preset = limitOrderPreset(order)
  return preset
    ? `P${preset.index + 1} (${fmtBps(preset.buy)} / ${fmtBps(preset.sell)})`
    : fmtBps(order.slippageBps)
}

/** The preset limitOrderModeLabel names — its index, and the buy / sell bps
 *  it trades on (the side the order goes out on, as it goes out) — or null
 *  when the order matches no preset (the label is then its slippage alone). */
export function limitOrderPreset(
  order: LimitOrder
): { index: number; buy: number; sell: number } | null {
  let index = order.presetIndex
  let buy = order.buySlippageBps
  let sell = order.sellSlippageBps
  if (index == null || buy == null || sell == null) {
    // A row from before orders recorded their preset. It is told by the
    // SHIPPED presets, the ones it was placed on — not by the saved set
    // (lib/tradePresetStore), which is the user's presets today: an edit there
    // must not rename an order of the past. A new order needs no guess: it is
    // placed with the saved preset's index and figures (Rail, submitLimit).
    const matched = DEFAULT_TRADE_PRESETS.findIndex((preset) =>
      order.side === 'buy'
        ? preset.buySlippageBps === order.slippageBps
        : preset.sellSlippageBps === order.slippageBps
    )
    if (matched < 0) return null
    const preset = DEFAULT_TRADE_PRESETS[matched]!
    index = matched
    buy = preset.buySlippageBps
    sell = preset.sellSlippageBps
  }
  if (index < 0 || index > 2) return null
  // the side it executes on, as it executes
  if (order.side === 'buy') buy = order.slippageBps
  else sell = order.slippageBps
  return { index, buy, sell }
}
